/* eslint-disable max-lines-per-function */
/* eslint-disable no-undefined */
import CardWrapper from '../wrapper/card';
import { CreateNotificationScheduledParams } from '../../params/redux/calendar';
import CustomDropdown from '../reusable/dropdown';
import CustomInput from '../reusable/input';
import { emailVariables } from '../../helper/lists';
import LabelWrapper from '../wrapper/label';
import { scheduleEmailSchema } from '../../schema/calendar_schema';
import { settingCalendarSelectedNotification } from '../../redux/slices/calendar';
import { useRef } from 'react';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { createCalendarLinkNotificationSchedulingThunk, createMeetingNotificationSchedulingThunk } from '../../redux/thunks/calendar';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

interface InitialValuesInterface {
    subject: string;
    days: string;
    hours: string;
    minutes: string;
    timing: 'before' | 'after';
    message: string;
}

const ScheduleEmailComponent = ({ onCancel }: { onCancel: (value: null) => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { selectedEvent, notificationDetails } = useSelector((state: RootState) => state.calendar);
    const initialValues: InitialValuesInterface = {
        subject: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.subject : '',
        days: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.schedule_day?.toString() : '',
        hours: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.schedule_hour?.toString() : '',
        minutes: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.schedule_min?.toString() : '',
        timing: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.schedule_type : 'before',
        message: notificationDetails?.selectedNotification ? notificationDetails?.selectedNotification?.message : ''
    };

    const onCancelHandler = () => {
        dispatch(settingCalendarSelectedNotification(null));
        onCancel(null);
    };

    const handleSubmit = async (values: InitialValuesInterface) => {
        try {
            const data: CreateNotificationScheduledParams = {
                calendar_id: selectedEvent?.type === 'link' ? selectedEvent?.id : undefined,
                event_id: selectedEvent?.type === 'meeting' ? selectedEvent?.id : undefined,
                subject: values.subject,
                message: values.message,
                type: 'email',
                notification_type: 'scheduled',
                schedule_day: parseInt(values.days, 10),
                schedule_hour: parseInt(values.hours, 10),
                schedule_type: values.timing,
                schedule_min: parseInt(values.minutes, 10),
                schedule_id: notificationDetails.selectedNotification ? notificationDetails.selectedNotification.id : 0,
                status: notificationDetails.selectedNotification ? notificationDetails.selectedNotification.status : 'enabled'
            };
            if (selectedEvent?.type === 'link') {
                await dispatch(createCalendarLinkNotificationSchedulingThunk(data)).unwrap();
            } else {
                await dispatch(createMeetingNotificationSchedulingThunk(data)).unwrap();
            }
            onCancelHandler();
        } catch (e) {
            //
        }
    };

    return <CardWrapper>
        <Typography variant="h4" align="center" gutterBottom>
            {notificationDetails.selectedNotification && 'Update'} Schedule Email
        </Typography>
        <Formik
            initialValues={initialValues}
            validationSchema={scheduleEmailSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => {
                const messageInputRef = useRef<HTMLTextAreaElement>(null);
                const subjectInputRef = useRef<HTMLInputElement>(null);
                const handleAddField = (field: string) => {
                    const textarea = subjectInputRef.current;
                    if (!textarea) {
                        // fallback to appending
                        setFieldValue('subject', `${values.subject} [${field}] `);
                        return;
                    }
                    const start = textarea.selectionStart || 0;
                    const end = textarea.selectionEnd || 0;
                    const insertText = `[${field}]`;
                    const newMessage =
                        values.subject.slice(0, start) +
                        insertText +
                        values.subject.slice(end);
                    setFieldValue('subject', newMessage);
                    // Restore focus and cursor after insertion
                    setTimeout(() => {
                        textarea.focus();
                        const cursorPos = start + insertText.length;
                        textarea.setSelectionRange(cursorPos, cursorPos);
                    }, 0);
                };
                const handleAddMessageField = (field: string) => {
                    const textarea = messageInputRef.current;
                    if (!textarea) {
                        // fallback to appending
                        setFieldValue('message', `${values.message} [${field}] `);
                        return;
                    }
                    const start = textarea.selectionStart || 0;
                    const end = textarea.selectionEnd || 0;
                    const insertText = `[${field}]`;
                    const newMessage =
                        values.message.slice(0, start) +
                        insertText +
                        values.message.slice(end);
                    setFieldValue('message', newMessage);
                    // Restore focus and cursor after insertion
                    setTimeout(() => {
                        textarea.focus();
                        const cursorPos = start + insertText.length;
                        textarea.setSelectionRange(cursorPos, cursorPos);
                    }, 0);
                };
                return <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <LabelWrapper label="Subject" sx={{ mb: 0 }} />
                                <Box>
                                    <CustomDropdown
                                        sx={{ mb: 0 }}
                                        value={''}
                                        // onChange={(e) => setFieldValue('subject', `${values.subject} [${e.target.value}]`)}
                                        onChange={(e) => handleAddField(e.target.value as string)}
                                        options={emailVariables.slice(0, 11).map((item) => ({ label: item, value: item }))}
                                    />
                                </Box>
                            </Box>
                            <CustomInput
                                mandatoryFields={true}
                                value={values.subject}
                                onChange={handleChange('subject')}
                                onBlur={handleBlur('subject')}
                                error={touched.subject && errors.subject}
                                fullWidth
                                placeholder="Subject"
                                variant="outlined"
                                inputRef={subjectInputRef}
                                disabled={false}
                            />
                        </Box>
                        <Box>
                            <LabelWrapper label="Set Time" isMandatory={true} />
                            <Box display="flex" gap={2} alignItems="center">
                                <CustomInput
                                    type="number"
                                    mandatoryFields={true}
                                    value={values.days}
                                    onChange={handleChange('days')}
                                    onBlur={handleBlur('days')}
                                    error={touched.days && errors.days}
                                    fullWidth
                                    placeholder="Days"
                                    variant="outlined"
                                    disabled={false}
                                />
                                <CustomInput
                                    type="number"
                                    mandatoryFields={true}
                                    value={values.hours}
                                    onChange={handleChange('hours')}
                                    onBlur={handleBlur('hours')}
                                    error={touched.hours && errors.hours}
                                    fullWidth
                                    placeholder="Hours"
                                    variant="outlined"
                                    disabled={false}
                                />
                                <CustomInput
                                    type="number"
                                    mandatoryFields={true}
                                    value={values.minutes}
                                    onChange={handleChange('minutes')}
                                    onBlur={handleBlur('minutes')}
                                    error={touched.minutes && errors.minutes}
                                    fullWidth
                                    placeholder="Minutes"
                                    variant="outlined"
                                    disabled={false}
                                />
                                <ToggleButtonGroup
                                    value={values.timing}
                                    exclusive
                                    onChange={(_, newVal) => newVal && setFieldValue('timing', newVal)}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <ToggleButton value="before">Before</ToggleButton>
                                    <ToggleButton value="after">After</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <LabelWrapper label="Message" sx={{ mb: 0 }} />
                                <Box>
                                    <CustomDropdown
                                        sx={{ mb: 0 }}
                                        value={''}
                                        // onChange={(e) => setFieldValue('subject', `${values.subject} [${e.target.value}]`)}
                                        onChange={(e) => handleAddMessageField(e.target.value as string)}
                                        options={emailVariables.slice(0, 11).map((item) => ({ label: item, value: item }))}
                                    />
                                </Box>
                            </Box>
                            <CustomInput
                                type="textarea"
                                value={values.message}
                                onChange={handleChange('message')}
                                onBlur={handleBlur('message')}
                                error={touched.message && errors.message}
                                fullWidth
                                placeholder="Type here"
                                variant="outlined"
                                disabled={false}
                                inputRef={messageInputRef}
                            />
                        </Box>
                        <Box display="flex" justifyContent="end" alignItems="center" gap={2}>
                            <Button variant="outlined" size="large" color="secondary" onClick={onCancelHandler}>Cancel</Button>
                            <Button variant="contained" size="large" type="submit">{notificationDetails.selectedNotification && 'Update'} Schedule</Button>
                        </Box>
                    </Box>
                </Form>;
            }}
        </Formik>
    </CardWrapper>;
};

export default ScheduleEmailComponent;